import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FileText, User, Clock, Check, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { ClinicalNoteListResponse, ClinicalNoteResponse } from '@/types/clinical.types';
import { NoteType, NoteStatus, NoteTypeLabels, NoteStatusLabels } from '@/types/clinical.types';

interface ClinicalNoteCardProps {
  note: ClinicalNoteListResponse | ClinicalNoteResponse;
  onClick?: () => void;
  showPatientName?: boolean;
}

export function ClinicalNoteCard({
  note,
  onClick,
  showPatientName = false,
}: ClinicalNoteCardProps) {
  const statusConfig = {
    [NoteStatus.DRAFT]: { color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
    [NoteStatus.SIGNED]: { color: 'bg-green-100 text-green-700', icon: Check },
    [NoteStatus.AMENDED]: { color: 'bg-blue-100 text-blue-700', icon: FileText },
    [NoteStatus.CANCELLED]: { color: 'bg-gray-100 text-gray-500', icon: AlertCircle },
  };

  const typeConfig = {
    [NoteType.SOAP]: 'bg-blue-100 text-blue-700',
    [NoteType.ADDENDUM]: 'bg-purple-100 text-purple-700',
    [NoteType.PRESCRIPTION]: 'bg-green-100 text-green-700',
    [NoteType.REFERRAL]: 'bg-orange-100 text-orange-700',
    [NoteType.CERTIFICATE]: 'bg-gray-100 text-gray-700',
    [NoteType.REPORT]: 'bg-cyan-100 text-cyan-700',
  };

  const StatusIcon = statusConfig[note.status].icon;

  return (
    <Card
      className={`transition-shadow ${onClick ? 'cursor-pointer hover:shadow-md' : ''}`}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <div>
              {showPatientName && (
                <p className="font-medium">{note.patientName}</p>
              )}
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={typeConfig[note.type]}>
                  {NoteTypeLabels[note.type]}
                </Badge>
                <Badge variant="outline" className={statusConfig[note.status].color}>
                  <StatusIcon className="mr-1 h-3 w-3" />
                  {NoteStatusLabels[note.status]}
                </Badge>
              </div>
            </div>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {format(new Date(note.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
            </div>
            <div className="text-xs">
              {format(new Date(note.createdAt), 'HH:mm', { locale: ptBR })}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Mostrar resumo do Subjetivo ou Avaliação */}
        {('subjectiveSummary' in note && note.subjectiveSummary) ? (
          <p className="mb-2 text-sm text-muted-foreground line-clamp-2">
            {note.subjectiveSummary}
          </p>
        ) : ('assessmentSummary' in note && note.assessmentSummary) ? (
          <p className="mb-2 text-sm text-muted-foreground line-clamp-2">
            {note.assessmentSummary}
          </p>
        ) : ('summary' in note && note.summary) ? (
          <p className="mb-2 text-sm text-muted-foreground line-clamp-2">
            {note.summary}
          </p>
        ) : null}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <User className="h-3 w-3" />
          {note.createdByName}
        </div>
      </CardContent>
    </Card>
  );
}

interface ClinicalNoteDetailProps {
  note: ClinicalNoteResponse;
}

export function ClinicalNoteDetail({ note }: ClinicalNoteDetailProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{NoteTypeLabels[note.type]}</h2>
          <p className="text-sm text-muted-foreground">
            {format(new Date(note.createdAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
              locale: ptBR,
            })}
          </p>
        </div>
        <Badge
          variant="outline"
          className={
            note.status === NoteStatus.SIGNED
              ? 'bg-green-100 text-green-700'
              : 'bg-yellow-100 text-yellow-700'
          }
        >
          {NoteStatusLabels[note.status]}
        </Badge>
      </div>

      {note.type === NoteType.SOAP && (
        <div className="grid gap-4">
          <SOAPSection title="S - Subjetivo" content={note.subjective} />
          <SOAPSection title="O - Objetivo" content={note.objective} />
          <SOAPSection title="A - Avaliação" content={note.assessment} />
          <SOAPSection title="P - Plano" content={note.plan} />
        </div>
      )}

      {note.problemsAddressed && note.problemsAddressed.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-medium">Problemas Abordados</h3>
          <div className="flex flex-wrap gap-2">
            {note.problemsAddressed.map((problem) => (
              <Badge key={problem.id} variant="secondary">
                {problem.cidCode && `${problem.cidCode} - `}
                {problem.description}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="border-t pt-4">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            <User className="mr-1 inline h-4 w-4" />
            Criado por {note.createdByName}
          </span>
          {note.signedAt && note.signedByName && (
            <span>
              <Check className="mr-1 inline h-4 w-4 text-green-500" />
              Assinado por {note.signedByName} em{' '}
              {format(new Date(note.signedAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SOAPSection({ title, content }: { title: string; content?: string }) {
  if (!content) return null;

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="mb-2 text-sm font-semibold text-primary">{title}</h3>
      <p className="whitespace-pre-wrap text-sm">{content}</p>
    </div>
  );
}
